import React, { FC, useState, Dispatch, SetStateAction } from 'react';
import DashboardContainer from 'views/Dashboard/DashboardContainer';
import { useNavigate } from 'react-router-dom';
import COButton from 'shared/components/COButton';
import { ButtonSize } from 'enums/ButtonEnums';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import styles from './addLocations.module.scss';
import AddIcon from 'assets/images/add_blue_bg.png';
import { ArrowRightOutlined } from '@ant-design/icons';
import { FieldArray, Form, Formik } from 'formik';
import { fetchGeocodeData } from 'shared/utils/fetchGeoLocation';
import { validationSchema } from './validation';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { CompanySetupEnum } from 'enums/CompanySetupEnums';
import {
  InitialValuesProp,
  initialValues,
  InitialValuesYears,
} from './constants';
import LocationFormFields from './AddLocationForm';
import HeadingSection from './Header';
import ParentCompanySetupService from 'services/ParentCompanySetup/parentCompanySetup.service';
import { notification } from 'antd';
import useFilters from 'shared/hooks/useFilters';
import { params, processingFacility } from 'constants/constants';
import LocationBoundaryModal from './LocationBoundaryModal';
import { setAddressInState } from './locationUtils';

const AddLocation: FC = () => {
  const navigate = useNavigate();
  const [initialValuesYears, setInitialValuesYears] = useState<{}>({});
  const { createLocations } = ParentCompanySetupService();
  const [openModalIndex, setOpenModalIndex] = useState<number | null>(null);
  const [fetchingAddress, setFetchingAddress] = useState<boolean>(false);
  const handleBack = () => navigate(AppRoutes.COMPANY_SETUP);
  const { getParams } = useFilters();
  const { isProcessingFacility: isPrefill, belongsId } = getParams(params);
  const searchPlaceByCountryProvince = async (
    country: string,
    province: string,
    setFieldValue: Function,
    index: number | string
  ) => {
    try {
      setFetchingAddress(true);
      const results = await fetchGeocodeData(`${province} ${country}`);
      if (results) {
        setFieldValue('map', results?.formattedAddress);
        setAddressInState(
          results?.addressComponents,
          results?.latLng,
          setFieldValue,
          `${index}`
        );
      }
    } catch (error) {
    } finally {
      setFetchingAddress(false);
    }
  };
  const handleAddLocation = (push: (value: {}) => void) => () => {
    push({
      name: '',
      category: isPrefill ? processingFacility : undefined,
      city: '',
      street: '',
      country: '',
      province: '',
      postalCode: '',
      belongs: belongsId ? String(belongsId) : undefined,
      latitude: '',
      longitude: '',
      countryCode: '',
      years: [],
    });
  };
  const handleFormSubmit = async (formData: InitialValuesProp) => {
    const nameSet = new Set();
    let hasError = false;

    formData.locations.forEach(location => {
      // Check if the location name is unique
      if (nameSet.has(location.name)) {
        notification.error({
          message: 'Location name should be unique',
          duration: 4,
        });
        hasError = true;
      } else {
        nameSet.add(location.name);
      }
      const yearsArray = location.years || [];
      // Check if the years array is empty
      if (!yearsArray.length) {
        notification.error({
          message: 'Please complete the Organization Boundary',
          duration: 4,
        });
        hasError = true;
      }
    });

    if (hasError) {
      return;
    }
    const payload = {
      data: {
        locations: formData.locations.map(location => {
          const {
            name,
            category,
            belongs,
            city,
            country,
            countryCode,
            postalCode,
            province,
            provinceCode,
            latitude,
            longitude,
            street,
            years,
          } = location;
          return {
            name: name,
            category: category,
            organization: {
              id: Number(belongs) || undefined,
            },
            address: {
              city: city,
              street: street,
              province: province,
              provinceCode: provinceCode,
              postalCode: postalCode || null,
              country: country,
              countryCode: countryCode,
              latitude: latitude.toString(),
              longitude: longitude.toString(),
            },
            years: years.map(year => ({
              year:
                typeof year.year === 'string' ? Number(year.year) : year.year,
              sharePercentage: Number(year.share_percentage),
            })),
          };
        }),
      },
    };

    try {
      const result = await createLocations(payload);
      if (result) {
        handleBack();
      }
    } catch (error) {}
  };

  const handleModalIndex = () => setOpenModalIndex(null);

  const initValues = {
    ...initialValues,
    locations: initialValues.locations.map((location, index) => ({
      ...location,
      belongs: belongsId ? String(belongsId) : undefined,
    })),
  };
  const handleRemoveLocation =
    (
      index: number,
      remove: (index: number) => void,
      setInitialValuesYears: Dispatch<
        SetStateAction<{ [key: number]: InitialValuesYears }>
      >
    ) =>
    () => {
      remove(index);
      setInitialValuesYears(prevState => {
        const newState = { ...prevState };
        delete newState[index];
        return newState;
      });
    };
  return (
    <>
      <DashboardContainer>
        <div className={styles.container}>
          <div>
            <div className={styles.formContainer}>
              <div className={styles.headerContainer}>
                <Formik
                  enableReinitialize
                  initialValues={initValues}
                  onSubmit={handleFormSubmit}
                  validationSchema={validationSchema}>
                  {({ setFieldValue, values }) => (
                    <Form>
                      <FieldArray name="locations">
                        {({ push, remove }) => (
                          <>
                            <HeadingSection
                              push={push}
                              fetchingAddress={fetchingAddress}
                            />
                            {values.locations.map((_, index: number) => {
                              return (
                                <div
                                  key={index}
                                  className={styles.locationsContainerArea}>
                                  <div className={styles.header}>
                                    <h3 className={styles.locationTitle}>
                                      {CompanySetupEnum.LOCATION} {index + 1}
                                    </h3>
                                    {values.locations.length > 1 && (
                                      <div
                                        onClick={handleRemoveLocation(
                                          index,
                                          remove,
                                          setInitialValuesYears
                                        )}
                                        className={styles.delContainer}>
                                        <DeleteIcon
                                          className={styles.deleteIcon}
                                        />
                                        {CompanySetupEnum.DELETE}
                                      </div>
                                    )}
                                  </div>
                                  <LocationFormFields
                                    index={index}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    searchPlaceByCountryProvince={
                                      searchPlaceByCountryProvince
                                    }
                                    setAddressInState={setAddressInState}
                                    setOpenModalIndex={setOpenModalIndex}
                                    openModalIndex={openModalIndex}
                                    fetchingAddress={fetchingAddress}
                                  />
                                  <LocationBoundaryModal
                                    index={index}
                                    openModalIndex={openModalIndex}
                                    handleModalIndex={handleModalIndex}
                                    initialValuesYears={initialValuesYears}
                                    setFieldValue={setFieldValue}
                                    setOpenModalIndex={setOpenModalIndex}
                                    setInitialValuesYears={
                                      setInitialValuesYears
                                    }
                                  />
                                </div>
                              );
                            })}
                            <div className={styles.addLocContainer}>
                              <div
                                className={styles.addLocation}
                                onClick={handleAddLocation(push)}>
                                <img src={AddIcon} className={styles.imgAdd} />
                                <span className={styles.addLocMargin}>
                                  {CompanySetupEnum.ADD_LOCATION}
                                </span>
                              </div>
                              <div>
                                <COButton
                                  primary
                                  size={ButtonSize.SMALL}
                                  submit
                                  loading={fetchingAddress}>
                                  <div className={styles.nextBtn}>
                                    {CompanySetupEnum.NEXT}
                                    <ArrowRightOutlined
                                      className={styles.arrowRight}
                                    />
                                  </div>
                                </COButton>
                              </div>
                            </div>
                          </>
                        )}
                      </FieldArray>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </DashboardContainer>
    </>
  );
};

export default AddLocation;
